import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';

interface DeletionCountdownBannerProps {
  onCancelSuccess: () => void;
}

interface DeletionStatus {
  id: string;
  user_id: string;
  deletion_requested_at: string;
  deletion_scheduled_at: string;
  reason?: string;
  is_cancelled: boolean;
  days_remaining: number;
}

const DeletionCountdownBanner: React.FC<DeletionCountdownBannerProps> = ({ onCancelSuccess }) => {
  const [deletionStatus, setDeletionStatus] = useState<DeletionStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkDeletionStatus();
  }, []);

  const checkDeletionStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setDeletionStatus(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/account-deletion-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.id) {
          setDeletionStatus(data);
        }
      } else if (response.status === 404) {
        // API endpoint not implemented yet - silently ignore
        setDeletionStatus(null);
      }
    } catch (error) {
      // Silently fail if backend is not available or endpoint doesn't exist
      setDeletionStatus(null);
    }
  };

  const handleCancelDeletion = async () => {
    if (!deletionStatus) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/cancel-account-deletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ deletion_id: deletionStatus.id }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to cancel account deletion');
      }

      setDeletionStatus(null);
      onCancelSuccess();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to cancel account deletion');
    } finally {
      setLoading(false);
    }
  };

  if (!deletionStatus) {
    return null;
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div style={{
      background: '#fff3cd',
      border: '1px solid #ffeaa7',
      padding: '10px',
      textAlign: 'center',
      fontSize: '14px',
    }}>
      <div style={{ marginBottom: '10px' }}>
        <strong>Account Deletion Scheduled</strong>
      </div>
      <div style={{ marginBottom: '10px' }}>
        Your account will be permanently deleted on {formatDate(deletionStatus.deletion_scheduled_at)}.
        {deletionStatus.days_remaining > 0 && (
          <span> ({deletionStatus.days_remaining} days remaining)</span>
        )}
      </div>
      
      {error && (
        <div style={{
          color: 'red',
          fontSize: '12px',
          marginBottom: '10px',
        }}>
          {error}
        </div>
      )}
      
      <button
        onClick={handleCancelDeletion}
        disabled={loading}
        style={{
          padding: '5px 15px',
          border: '1px solid #ccc',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '12px',
          background: 'white',
        }}
      >
        {loading ? 'Cancelling...' : 'Cancel Deletion'}
      </button>
    </div>
  );
};

export default DeletionCountdownBanner;